import {
  cropsEqual,
  persistableCrop,
} from "../../../../features/creativeTemplates/editor/layerCrop";
import { areHighlightListsEqual } from "../../../../features/creativeTemplates/utils/textHighlights";
import { areInlineReplacementListsEqual } from "../../../../features/creativeTemplates/utils/textInlineReplacements";

export const IMAGE_EDIT_WORK_MODE = {
  GENERATIVE: "generative",
  LAYERS: "layers",
};

export const LAYERS_NORMALIZED_MAX = 1000;
export const LAYERS_UNDO_LIMIT = 50;

export const LAYER_TYPE = {
  IMAGE: "image",
  TEXT: "text",
};

export const DEFAULT_TEXT_LAYER_STYLE = {
  fontSize: 60,
  color: "#FFFFFF",
  align: "left",
  orientation: "horizontal",
  textStyle: "normal",
};

export const getLayerType = (layer = {}) =>
  layer?.type === LAYER_TYPE.TEXT ? LAYER_TYPE.TEXT : LAYER_TYPE.IMAGE;

export const isLayerVisible = (layer = {}) => layer?.visible !== false;

export const cloneLayers = (layers = []) =>
  layers.map((layer) => ({
    ...layer,
    style: layer?.style ? { ...layer.style } : layer?.style,
  }));

export const isSameLayerGeometry = (layer, box) =>
  Math.abs((Number(layer?.x) || 0) - (Number(box?.x) || 0)) < 0.001 &&
  Math.abs((Number(layer?.y) || 0) - (Number(box?.y) || 0)) < 0.001 &&
  Math.abs((Number(layer?.width) || 0) - (Number(box?.width) || 0)) < 0.001 &&
  Math.abs((Number(layer?.height) || 0) - (Number(box?.height) || 0)) < 0.001 &&
  Math.abs((Number(layer?.rotation) || 0) - (Number(box?.rotation) || 0)) <
    0.01;

export const isSameLayerBox = (layer, box) =>
  isSameLayerGeometry(layer, box) && cropsEqual(layer?.crop, box?.crop);

export const applyLayerBoxPatch = (layer, box) => {
  const next = {
    ...layer,
    x: box.x,
    y: box.y,
    width: box.width,
    height: box.height,
    rotation: Number(box.rotation) || 0,
  };
  if (!Object.prototype.hasOwnProperty.call(box || {}, "crop")) return next;
  const nextCrop = persistableCrop(box.crop);
  if (nextCrop) next.crop = nextCrop;
  else delete next.crop;
  return next;
};

const isSameLayerStyle = (left, right) => {
  const first = left?.style || {};
  const second = right?.style || {};
  return (
    (first.fontSize ?? "") === (second.fontSize ?? "") &&
    (first.lineHeight ?? "") === (second.lineHeight ?? "") &&
    (first.letterSpacing ?? "") === (second.letterSpacing ?? "") &&
    (first.strokeWidth ?? "") === (second.strokeWidth ?? "") &&
    (first.strokeColor || "") === (second.strokeColor || "") &&
    (first.shadowColor || "") === (second.shadowColor || "") &&
    (first.shadowAngle ?? "") === (second.shadowAngle ?? "") &&
    (first.shadowDistance ?? "") === (second.shadowDistance ?? "") &&
    (first.shadowBlur ?? "") === (second.shadowBlur ?? "") &&
    (first.backgroundPadding ?? "") === (second.backgroundPadding ?? "") &&
    (first.backgroundRadius ?? "") === (second.backgroundRadius ?? "") &&
    (first.backgroundColor || "") === (second.backgroundColor || "") &&
    (first.color || "") === (second.color || "") &&
    (first.align || "") === (second.align || "") &&
    (first.orientation || "") === (second.orientation || "") &&
    (first.fontAssetRef || "") === (second.fontAssetRef || "") &&
    (first.fontFaceRef || "") === (second.fontFaceRef || "") &&
    (first.fontWeight ?? "") === (second.fontWeight ?? "") &&
    (first.textStyle || "normal") === (second.textStyle || "normal") &&
    areHighlightListsEqual(first.highlights, second.highlights) &&
    areInlineReplacementListsEqual(
      first.inlineReplacements,
      second.inlineReplacements
    )
  );
};

export const areLayerListsEqual = (left = [], right = []) =>
  left.length === right.length &&
  left.every(
    (layer, index) =>
      layer?.id === right[index]?.id &&
      (Number(layer?.zIndex) || 0) === (Number(right[index]?.zIndex) || 0) &&
      (layer?.name || "") === (right[index]?.name || "") &&
      (layer?.type || "") === (right[index]?.type || "") &&
      (layer?.text || "") === (right[index]?.text || "") &&
      (layer?.url || "") === (right[index]?.url || "") &&
      Boolean(layer?.locked) === Boolean(right[index]?.locked) &&
      Boolean(layer?.visible !== false) ===
        Boolean(right[index]?.visible !== false) &&
      isSameLayerStyle(layer, right[index]) &&
      isSameLayerBox(layer, right[index])
  );

export const createTextLayer = ({ id, name, zIndex, style } = {}) => ({
  id,
  type: LAYER_TYPE.TEXT,
  name: name || null,
  description: null,
  text: "",
  url: null,
  zIndex,
  isBackground: false,
  x: LAYERS_NORMALIZED_MAX * 0.12,
  y: LAYERS_NORMALIZED_MAX * 0.12,
  width: LAYERS_NORMALIZED_MAX * 0.4,
  height: LAYERS_NORMALIZED_MAX * 0.12,
  style: { ...DEFAULT_TEXT_LAYER_STYLE, ...(style || {}) },
});

const LAYER_KIND_LOGO_PATTERN = /\blogo\b/i;
const LAYER_KIND_TEXT_PATTERN =
  /\b(text|texto|headline|title|t[íi]tulo|copy|caption|cta|llamado)\b/i;

export const LAYER_KIND = {
  IMAGE: "image",
  OBJECT: "object",
  TEXT: "text",
  LOGO: "logo",
};

export const getLayerKind = (layer = {}) => {
  if (layer.isBackground) return LAYER_KIND.IMAGE;
  const haystack = `${layer.name || ""} ${layer.description || ""}`;
  if (LAYER_KIND_LOGO_PATTERN.test(haystack)) return LAYER_KIND.LOGO;
  if (LAYER_KIND_TEXT_PATTERN.test(haystack)) return LAYER_KIND.TEXT;
  if (String(layer.id || "").startsWith("imported_")) return LAYER_KIND.IMAGE;
  return LAYER_KIND.OBJECT;
};

/**
 * Maps sidebar display order (front to back, top of list first) to zIndex.
 * The first displayed layer is painted last (highest zIndex).
 */
export const applyDisplayOrderToZIndex = (
  layers = [],
  orderedDisplayIds = []
) => {
  const byId = new Map(layers.map((layer) => [layer.id, layer]));
  const ordered = [];
  const seen = new Set();

  for (const id of orderedDisplayIds) {
    const layer = byId.get(id);
    if (!layer || seen.has(id)) continue;
    ordered.push(layer);
    seen.add(id);
  }

  for (const layer of layers) {
    if (!seen.has(layer.id)) {
      ordered.push(layer);
    }
  }

  const lastIndex = ordered.length - 1;
  return ordered
    .map((layer, displayIndex) => ({
      ...layer,
      zIndex: lastIndex - displayIndex,
    }))
    .sort(
      (left, right) => (Number(left.zIndex) || 0) - (Number(right.zIndex) || 0)
    );
};

export const layersRecordToArray = (layers = {}) =>
  Object.values(layers || {}).sort(
    (left, right) => (left.zIndex || 0) - (right.zIndex || 0)
  );

export const layersArrayToRecord = (layers = []) =>
  layers.reduce((record, layer) => {
    if (!layer?.id) return record;
    record[layer.id] = layer;
    return record;
  }, {});

export const normalizedToPixels = ({
  x,
  y,
  width,
  height,
  rotation,
  displayWidth,
  displayHeight,
}) => ({
  x: ((Number(x) || 0) / LAYERS_NORMALIZED_MAX) * displayWidth,
  y: ((Number(y) || 0) / LAYERS_NORMALIZED_MAX) * displayHeight,
  width: ((Number(width) || 0) / LAYERS_NORMALIZED_MAX) * displayWidth,
  height: ((Number(height) || 0) / LAYERS_NORMALIZED_MAX) * displayHeight,
  rotation: Number(rotation) || 0,
});

export const pixelsToNormalized = ({
  x,
  y,
  width,
  height,
  rotation,
  displayWidth,
  displayHeight,
}) => {
  const toNormalized = (value, size) =>
    size ? (Number(value) / size) * LAYERS_NORMALIZED_MAX : 0;

  return {
    x: toNormalized(x, displayWidth),
    y: toNormalized(y, displayHeight),
    width: Math.max(0, toNormalized(width, displayWidth)),
    height: Math.max(0, toNormalized(height, displayHeight)),
    rotation: Number(rotation) || 0,
  };
};

export const layerToInspectorItem = ({ layer, canvasWidth, canvasHeight }) => {
  if (!layer) return null;
  const type = getLayerType(layer);
  return {
    id: layer.id,
    url: layer.url || "",
    text: layer.text || "",
    isBackground: Boolean(layer.isBackground),
    node: {
      id: layer.id,
      name: layer.name || "",
      type,
      style: layer.style || {},
      source: { value: layer.text || "" },
    },
    box: normalizedToPixels({
      x: layer.x,
      y: layer.y,
      width: layer.width,
      height: layer.height,
      displayWidth: canvasWidth || 0,
      displayHeight: canvasHeight || 0,
    }),
  };
};

const isLocalObjectUrl = (url = "") =>
  url.startsWith("blob:") || url.startsWith("data:");

const loadImageElement = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Failed to load image"));
    image.src = url;
  });

/** Loads an image drawable on canvas. Remote URLs are fetched as blobs to avoid a non-CORS cached `<img>` response. */
export const loadHtmlImage = async (url) => {
  if (!url) {
    throw new Error("Missing image URL");
  }

  if (isLocalObjectUrl(url)) {
    return loadImageElement(url);
  }

  const response = await fetch(url, { mode: "cors", cache: "no-store" });
  if (!response.ok) {
    throw new Error(`Failed to load image (${response.status})`);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    return await loadImageElement(objectUrl);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};
